/**
 * Core-100 R2 — CORE-AA / CORE-AG-GAP-001: the ticket's history as ONE ordered sequence across its
 * stores (`reconstruct_ticket_history`), each row naming where it came from, and the list of stores
 * the reconstruction covers (`ticket_history_sources`) — so a reader can see what is NOT in it
 * instead of assuming nothing else happened. A failed read says so; it is not an empty history.
 */
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';

export interface HistoryRow {
  occurred_at: string; source: string; kind: string; actor_user_id: string | null; detail: Record<string, unknown> | null; event_id: string;
  /** Core-100 R4 (CORE-AT GAP-002): the ticket the row happened on, and how it relates to this one. */
  via_ticket_id?: string | null; lineage?: 'self' | 'split_parent' | 'merged_from' | string | null;
  /**
   * Core-100 R4 (CORE-AG): WHO, never blank — 'person' | 'customer' | 'system' | 'automation' | 'unrecorded' — and the
   * person's display name when they are or were a member of this shop (migration 20270206020600).
   */
  actor_kind?: 'person' | 'customer' | 'system' | 'automation' | 'unrecorded' | string | null;
  actor_name?: string | null;
}
interface SourceRow { source: string; store: string; rank: number }

export const SOURCE_LABEL: Record<string, string> = {
  thread: 'Timeline', business_state: 'State', custody: 'Custody', timer: 'Timer',
  pause: 'Pause', location: 'Location', rollover: 'Carry-over', finish: 'Finish',
  correction: 'Correction', lineage: 'Split / merge',
};

/**
 * Core-100 R4 (CORE-AG): who did it, in words. A system act reads as the system, an automated process as automatic,
 * and a store that did not record its actor says so — never a blank. Operational, not a judgement of anyone.
 */
export function actorText(r: HistoryRow): string {
  switch (r.actor_kind) {
    case 'person': return r.actor_name ? `by ${r.actor_name}` : 'by a team member';
    case 'customer': return 'by the customer';
    case 'system': return 'by OptiBay (system)';
    case 'automation': return 'automatically (sensor / AI)';
    case 'unrecorded': return 'who: not recorded';
    default: return r.actor_user_id ? 'by a team member' : 'who: not recorded';
  }
}

/** Where an ancestor's row came from, in words (null for the ticket's own rows). */
export function lineageNote(r: HistoryRow): string | null {
  if (!r.lineage || r.lineage === 'self') return null;
  return r.lineage === 'merged_from' ? 'on a part merged into this ticket' : 'on the original ticket, before the split';
}

export function describeHistoryRow(r: HistoryRow): string {
  const d = r.detail ?? {};
  switch (r.source) {
    case 'thread': return typeof d.content === 'string' && d.content ? d.content : r.kind.replace(/_/g, ' ');
    case 'business_state': return `${String(d.from ?? '—')} → ${String(d.to ?? '—')}`;
    case 'custody': return `${r.kind.replace(/_/g, ' ')} (${String(d.from ?? '—')} → ${String(d.to ?? '—')})`;
    case 'timer': return r.kind === 'timer_started' ? 'Timer started' : `Timer stopped${d.duration_min != null ? ` after ${d.duration_min} min` : ''}`;
    case 'pause': return r.kind === 'paused' ? `Paused${d.reason ? ` — ${String(d.reason)}` : ''}` : 'Resumed';
    case 'location': return `Location: ${r.kind.replace(/_/g, ' ').toLowerCase()}`;
    case 'rollover': return 'Carried over to the next day';
    case 'finish': return d.opti_finish ? `Finish time recalculated${d.efficiency_source ? ` (efficiency: ${String(d.efficiency_source)})` : ''}` : 'Finish time cleared';
    case 'correction': return `Completion corrected (${r.kind.replace(/_/g, ' ')})${d.reason ? ` — ${String(d.reason)}` : ''}`;
    case 'lineage': return r.kind === 'merged_into_successor' ? 'This part was merged into its successor' : 'The original ticket was split into parts';
    default: return r.kind;
  }
}

export function TicketReconstructedHistory({ ticketId, className }: { ticketId: string; className?: string }) {
  const history = useQuery({
    queryKey: ['ticket-history-reconstruction', ticketId],
    queryFn: async (): Promise<HistoryRow[]> => {
      const { data, error } = await supabase.rpc('reconstruct_ticket_history' as never, { p_ticket_id: ticketId } as never);
      if (error) throw error;
      return ((data ?? []) as unknown) as HistoryRow[];
    },
  });
  const sources = useQuery({
    queryKey: ['ticket-history-sources'],
    queryFn: async (): Promise<SourceRow[]> => {
      const { data, error } = await supabase.rpc('ticket_history_sources' as never);
      if (error) throw error;
      return ((data ?? []) as unknown) as SourceRow[];
    },
    staleTime: Infinity,
  });
  return (
    <ScrollArea className={className}>
      <div className="px-6 py-4 space-y-3" data-testid="ticket-reconstructed-history">
        {history.isLoading && <p className="text-sm text-muted-foreground">Loading history…</p>}
        {history.isError && <p className="text-sm text-muted-foreground" data-testid="ticket-history-unreadable">The history could not be loaded. This is not the same as there being none.</p>}
        {history.data && history.data.length === 0 && <p className="text-sm text-muted-foreground">Nothing recorded yet.</p>}
        <ol className="space-y-2">
          {history.data?.map((r) => (
            <li key={`${r.source}-${r.event_id}-${r.kind}`} className="text-sm flex gap-2 items-start">
              <span className="text-xs text-muted-foreground w-28 shrink-0">{format(new Date(r.occurred_at), 'MMM d, h:mm a')}</span>
              <Badge variant="outline" className="shrink-0">{SOURCE_LABEL[r.source] ?? r.source}</Badge>
              <span>{describeHistoryRow(r)}{lineageNote(r) && <span className="text-xs text-muted-foreground" data-testid="ticket-history-lineage"> ({lineageNote(r)})</span>}
                <span className="block text-xs text-muted-foreground" data-testid="ticket-history-actor" data-actor-kind={r.actor_kind ?? ''}>{actorText(r)}</span>
              </span>
            </li>
          ))}
        </ol>
        {sources.data && (
          <p className="text-[11px] text-muted-foreground" data-testid="ticket-history-coverage">
            Covers: {sources.data.map((s) => SOURCE_LABEL[s.source] ?? s.source).join(', ')}. Other records (for example messages and audit entries) are not part of this list.
          </p>
        )}
      </div>
    </ScrollArea>
  );
}
